/* eslint-disable react-refresh/only-export-components */
/**
 * Aceternity UI Sidebar Component
 *
 * Hover-to-expand sidebar with Framer Motion animations.
 * Based on: https://ui.aceternity.com/components/sidebar
 */

import { createContext, useContext, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'

// ============================================
// Types
// ============================================

interface Links {
  label: string
  href: string
  icon: React.ReactNode
  showBadge?: boolean
}

interface SidebarContextProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  animate: boolean
}

// ============================================
// Context
// ============================================

const SidebarContext = createContext<SidebarContextProps | undefined>(undefined)

export const useSidebar = () => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

// ============================================
// Provider
// ============================================

interface SidebarProviderProps {
  children: React.ReactNode
  open?: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
  animate?: boolean
}

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: SidebarProviderProps) => {
  const [openState, setOpenState] = useState(false)

  const open = openProp !== undefined ? openProp : openState
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate }}>
      {children}
    </SidebarContext.Provider>
  )
}

// ============================================
// Main Sidebar Component
// ============================================

interface SidebarProps {
  children: React.ReactNode
  open?: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
  animate?: boolean
}

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate = true,
}: SidebarProps) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  )
}

// ============================================
// Sidebar Body
// ============================================

interface SidebarBodyProps {
  className?: string
  children: React.ReactNode
}

export const SidebarBody = ({ className, children }: SidebarBodyProps) => {
  return (
    <>
      <DesktopSidebar className={className}>{children}</DesktopSidebar>
      <MobileSidebar className={className}>{children}</MobileSidebar>
    </>
  )
}

// ============================================
// Desktop Sidebar
// ============================================

interface DesktopSidebarProps {
  className?: string
  children: React.ReactNode
}

export const DesktopSidebar = ({ className, children }: DesktopSidebarProps) => {
  const { open, setOpen, animate } = useSidebar()

  return (
    <motion.div
      className={cn(
        'h-screen px-3 py-4 hidden md:flex md:flex-col bg-card border-r shrink-0',
        className
      )}
      animate={{
        width: animate ? (open ? '240px' : '64px') : '240px',
      }}
      initial={{
        width: '240px',
      }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {children}
    </motion.div>
  )
}

// ============================================
// Mobile Sidebar
// ============================================

interface MobileSidebarProps {
  className?: string
  children: React.ReactNode
}

export const MobileSidebar = ({ className, children }: MobileSidebarProps) => {
  const { open, setOpen } = useSidebar()

  return (
    <>
      {/* Mobile Header */}
      <div
        className={cn(
          'h-14 px-4 flex flex-row md:hidden items-center justify-between bg-card border-b w-full'
        )}
      >
        <div className="flex items-center gap-2">
          <Menu
            className="h-5 w-5 text-muted-foreground cursor-pointer"
            onClick={() => setOpen(!open)}
          />
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
              }}
              className="fixed inset-0 bg-black/50 z-[99] md:hidden"
              onClick={() => setOpen(false)}
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '-100%', opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
              }}
              className={cn(
                'fixed h-full w-full inset-0 bg-card p-6 z-[100] flex flex-col justify-between md:hidden',
                className
              )}
            >
              <div
                className="absolute right-6 top-6 text-muted-foreground cursor-pointer"
                onClick={() => setOpen(!open)}
              >
                <X className="h-5 w-5" />
              </div>
              {children}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

// ============================================
// Sidebar Link
// ============================================

interface SidebarLinkProps {
  link: Links
  className?: string
  onClick?: () => void
}

export const SidebarLink = ({ link, className, onClick }: SidebarLinkProps) => {
  const { open, animate, setOpen } = useSidebar()
  const location = useLocation()
  const isActive = location.pathname === link.href

  const handleClick = () => {
    // Close sidebar on mobile when link is clicked
    setOpen(false)
    // Call any additional onClick handler
    onClick?.()
  }

  return (
    <Link
      to={link.href}
      onClick={handleClick}
      className={cn(
        'flex items-center gap-2 py-2 px-2 rounded-md transition-colors group/sidebar',
        isActive
          ? 'bg-secondary text-secondary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        className
      )}
      title={!open ? link.label : undefined}
    >
      <span className="shrink-0 relative">
        {link.icon}
        {/* Badge dot - visible when sidebar collapsed */}
        {link.showBadge && !open && (
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-sieve-500 rounded-full animate-pulse-soft" />
        )}
      </span>
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="text-sm whitespace-nowrap flex items-center gap-2"
      >
        {link.label}
        {/* Badge dot - visible when sidebar expanded */}
        {link.showBadge && open && (
          <span className="h-2 w-2 bg-sieve-500 rounded-full animate-pulse-soft" />
        )}
      </motion.span>
    </Link>
  )
}

// ============================================
// Sidebar Section Header
// ============================================

interface SidebarSectionProps {
  label: string
  className?: string
}

export const SidebarSection = ({ label, className }: SidebarSectionProps) => {
  const { open, animate } = useSidebar()

  return (
    <motion.div
      animate={{
        display: animate ? (open ? 'block' : 'none') : 'block',
        opacity: animate ? (open ? 1 : 0) : 1,
      }}
      className={cn(
        'px-2 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider',
        className
      )}
    >
      {label}
    </motion.div>
  )
}

// ============================================
// Logo Component with Animation
// ============================================

interface LogoProps {
  icon?: React.ReactNode
  text: string
  subtext?: string
  className?: string
  href?: string
}

export const Logo = ({ icon, text, subtext, className, href }: LogoProps) => {
  const { open, animate } = useSidebar()

  const content = (
    <>
      {icon && <span className="shrink-0 flex items-center justify-center w-7 h-7">{icon}</span>}
      <motion.div
        animate={{
          width: animate ? (open ? 'auto' : 0) : 'auto',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="flex flex-col overflow-hidden"
      >
        <span className="font-bold text-lg text-sieve whitespace-nowrap">{text}</span>
        {subtext && (
          <span className="text-xs text-muted-foreground truncate max-w-[160px]">{subtext}</span>
        )}
      </motion.div>
    </>
  )

  const baseClasses = cn(
    'flex items-center gap-2 py-2 overflow-hidden',
    href && 'cursor-pointer hover:opacity-80 transition-opacity',
    className
  )

  if (href) {
    return (
      <Link to={href} className={baseClasses}>
        {content}
      </Link>
    )
  }

  return <div className={baseClasses}>{content}</div>
}
